import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PasswordTextField from './PasswordTextField'
import AuthCenterClient from 'auth-center-js-client'
import { TextFieldType } from 'foundations-library/types'
import PasswordStrengthBars from './PasswordStrengthBars'
import { Icon, Typography } from 'foundations-library/components'

const client = new AuthCenterClient()

const newPasswordHelperText = 'Please add all necessary characters to create a safe password'
const newPasswordValidationError = (newPasswordFieldHasBeenChanged: boolean, checkPassed: boolean) => {
    return <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
        <Icon name='notification-alert' size={16} color={newPasswordFieldHasBeenChanged && !checkPassed ? 'destructive-500' : 'neutral-400'} />
        <Typography variant='body4' color={newPasswordFieldHasBeenChanged && !checkPassed ? 'destructive-500' : 'neutral-400'}>{newPasswordHelperText}</Typography>
    </div>
}

const jss = (t) => {
    const { colors } = t['foundations-library']
    return {
        validationContainer: {
            marginBottom: '20px',
            marginTop: '15px'
        },
        passwordStrengthBars: {
            marginBottom: '15px'
        },
        validationInfoContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            alignSelf: 'stretch'
        },
        list: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            alignSelf: 'stretch'
        },
        newPasswordValidationMessage: {
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            alignSelf: 'stretch'
        },
        circleIconContainer: {
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
            height: '18px',
            width: '18px'
        },
        circleIcon: {
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            '&.neutral': {
                backgroundColor: colors.neutral[400]
            },
            '&.destructive': {
                backgroundColor: colors.destructive[500]
            }
        }
    }
}

const useStyles = makeStyles(jss)

/**
 *
 */
const NewPasswordTextField = (props: TextFieldType): JSX.Element => {
    const styles = useStyles()
    const [newPasswordFieldHasBeenChanged, setNewPasswordFieldHasBeenChanged] = useState(false)
    const [validationResults, setValidationResults] = useState({})

    useEffect(() => {
        setNewPasswordFieldHasBeenChanged((props.value as string).length > 0)
        client.checkNewUserPassword({ password: props.value, localChecksOnly: true })
            .then((rulesResponse) => {
                setValidationResults(rulesResponse)
            })
    }, [props.value])

    useEffect(() => {
        props.isValid && props.isValid(!!validationResults.checkPassed)
    }, [validationResults?.checkPassed])

    return <div>
        <PasswordTextField
            label='New Password'
            name="new-password"
            autoComplete='new-password'
            minLength={12}
            maxLength={20}
            pattern="^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{12,20}$"
            data-testid='new-password'
            aria-describedby='password-constraints password-note'
            helperText={newPasswordValidationError(newPasswordFieldHasBeenChanged, validationResults.checkPassed)}
            className={newPasswordFieldHasBeenChanged && !validationResults.checkPassed ? 'has-error' : ''}
            {...props}
        />
        {
            validationResults.checkStatus?.length && <div className={styles.validationContainer}>
                <PasswordStrengthBars
                    total={validationResults.summary.passCount + validationResults.summary.failCount}
                    completed={
                        !newPasswordFieldHasBeenChanged && !props.value ? 0 : validationResults.summary?.passCount
                    }
                    className={styles.passwordStrengthBars}
                />
                <div className={styles.validationInfoContainer}>
                    <div className={styles.list} id='password-constraints'>
                        {validationResults.checkStatus.map((rule) => {
                            const circleIcon = (color: string) => (
                                <div className={styles.circleIconContainer}>
                                    <div className={`${styles.circleIcon} ${color}`} />
                                </div>
                            )
                            const checkIcon = (color: string) => (<Icon name='check' size={18} color={color} />)

                            return (
                                <div className={styles.newPasswordValidationMessage} data-testid={`rule-${rule.id}`} key={rule.id}>
                                    { !newPasswordFieldHasBeenChanged ? circleIcon('neutral') : rule.passed ? checkIcon('primary-500') : circleIcon('destructive') }
                                    <Typography variant='body3' color={!newPasswordFieldHasBeenChanged ? 'neutral-400' : rule.passed ? 'primary-500' : 'destructive-500'}>{rule.description}</Typography>
                                </div>
                            )
                        })}
                    </div>
                    <div data-testid='password-note' id='password-note'>
                        <Typography tag='span' variant='subtitle3' color='neutral-700' className='password-management-bottom-note-bold'>Note:</Typography>
                        &nbsp;
                        <Typography tag='span' variant='body3' color='neutral-500' remoteConfigID='password_management_bottom_note' />
                    </div>
                </div>
            </div>
        }
    </div>
}

export default NewPasswordTextField
